/**
 * postprocessing-chain.ts — pmndrs/postprocessing EffectComposer chain.
 *
 * Plan 14a, Task 9.1.
 *
 * Builds an EffectComposer pipeline (RenderPass → EffectPass with Bloom,
 * SMAA, ChromaticAberration, and optionally LUT3D) and wraps it in the
 * ComposerMount interface.  When usePostprocessing is false the chain is
 * not constructed and render() falls through directly to
 * renderer.render(scene, camera).
 *
 * Effect order in EffectPass follows pmndrs best-practice:
 *   BloomEffect → SMAAEffect → ChromaticAberrationEffect → LUT3DEffect
 *
 * Defaults per spec:
 *   Bloom:      intensity 0.6, luminanceThreshold 0.85
 *   SMAA:       library defaults
 *   CA:         offset new THREE.Vector2(0.0008, 0.0008)
 *   LUT:        only added when config.lut is non-null / non-undefined
 */

import * as THREE from 'three';
import {
  Effect,
  EffectComposer,
  RenderPass,
  EffectPass,
  BloomEffect,
  SMAAEffect,
  ChromaticAberrationEffect,
  LUT3DEffect,
} from 'postprocessing';
import type { BloomControlHandle, ComposerMount } from '../types';
import type { PostprocessingConfig } from './types';

function createBloomControl(bloom: BloomEffect): BloomControlHandle {
  return {
    get strength(): number {
      return bloom.intensity;
    },
    set strength(value: number) {
      bloom.intensity = value;
    },
    get radius(): number {
      return bloom.mipmapBlurPass.radius;
    },
    set radius(value: number) {
      bloom.mipmapBlurPass.radius = value;
    },
    get threshold(): number {
      return bloom.luminanceMaterial.threshold;
    },
    set threshold(value: number) {
      bloom.luminanceMaterial.threshold = value;
    },
  };
}

/**
 * Creates a ComposerMount backed by an EffectComposer pipeline when
 * config.usePostprocessing is true, or a simple pass-through when false.
 *
 * @param renderer - The THREE.WebGLRenderer to use.
 * @param scene    - The scene passed to RenderPass.
 * @param camera   - The camera passed to RenderPass + EffectPass.
 * @param config   - Postprocessing feature flags and effect parameters.
 */
export function createPostprocessingChain(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  config: PostprocessingConfig,
): ComposerMount {
  // ── Non-postprocessing path ───────────────────────────────────────────────
  if (!config.usePostprocessing) {
    let _disposed = false;
    return {
      render(s: THREE.Scene, c: THREE.Camera): void {
        renderer.render(s, c);
      },
      attachPass(_pass: unknown): never {
        throw new Error('ComposerMount.attachPass: no-op in pass-through mode');
      },
      onResize(_width: number, _height: number): void {
        // Nothing to resize in pass-through mode; renderer.setSize is
        // called by SceneHandle.onResize.
      },
      dispose(): void {
        _disposed = true;
        void _disposed;
      },
    };
  }

  // ── Build effects ─────────────────────────────────────────────────────────
  const effects: Effect[] = [];
  let bloomControl: BloomControlHandle | undefined;

  if (config.bloom !== false) {
    const bloom = new BloomEffect({
      intensity: 0.6,
      luminanceThreshold: 0.85,
      mipmapBlur: true,
      radius: 0.85,
    });
    bloomControl = createBloomControl(bloom);
    effects.push(bloom);
  }

  if (config.smaa !== false) {
    effects.push(new SMAAEffect());
  }

  if (config.chromaticAberration !== false) {
    effects.push(
      new ChromaticAberrationEffect({
        offset: new THREE.Vector2(0.0008, 0.0008),
        // Type def has radialModulation + modulationOffset as required (non-optional).
        // Explicitly pass false/default to satisfy the type without enabling radial mode.
        radialModulation: false,
        modulationOffset: 0.15,
      }),
    );
  }

  if (config.lut != null) {
    effects.push(new LUT3DEffect(config.lut));
  }

  // ── Build composer ────────────────────────────────────────────────────────
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(new EffectPass(camera, ...effects));

  let _disposed = false;

  return {
    bloom: bloomControl,
    render(_scene: THREE.Scene, _camera: THREE.Camera): void {
      // deltaTime not tracked here; caller owns the rAF loop.
      // EffectComposer.render(deltaTime?) is optional param — call with no
      // args to use the composer's internal timer.
      composer.render();
    },

    attachPass(_pass: unknown): never {
      // Plan 14a: chain is fully constructed at creation time.
      // attachPass is kept as a no-op that throws to satisfy the
      // ComposerMount interface contract without enabling arbitrary
      // pass injection after construction (which would bypass our
      // controlled effect ordering).
      throw new Error(
        'ComposerMount.attachPass: use createPostprocessingChain() options to configure the chain',
      );
    },

    onResize(width: number, height: number): void {
      composer.setSize(width, height);
    },

    dispose(): void {
      if (_disposed) return;
      _disposed = true;
      composer.dispose();
    },
  };
}
